import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule, DOCUMENT, isPlatformServer, NgClass } from '@angular/common';
import { catchError, distinctUntilChanged, skip, Subscription, take, tap, throwError } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { routes } from '../../../constants/routes';
import { ApiService } from '../../../services/api.service';
import { UtilityService } from '../../../services/utility.service';
import { ToasterService } from '../../../services/toaster.service';
import { GlobalSearchComponent } from '../../utility/global-search/global-search.component';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { ApiErrorHandler } from '../../../models/api-error-handler.model';
import { environment } from '../../../../environments/environment';
import { cachingKeys } from '../../../constants/caching-keys';
import { CachingService } from '../../../services/caching.service';
import { ConfirmationDialogComponent } from '../../utility/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { orgBlackSVG } from '../../../constants/svg/organisation';
import { SafeHtmlPipe } from '../../../pipes/safe-html.pipe';
import { profileBlackSVG } from '../../../constants/svg/profile-black';
import { accountSettingSVG } from '../../../constants/svg/account-setting';
import { helpBlackSVG } from '../../../constants/svg/help-black';
import { arrowRightBlackSVG } from '../../../constants/svg/arrow-right-black';
import { logoutDangerSVG } from '../../../constants/svg/logout-danger';
import { LoggedInUser } from '../../../models/logged-in-user.model';
import { ROLES } from '../../../constants/roles';
import { FormatRolesPipe } from '../../../pipes/format-roles.pipe';
import { ImagePlaceholderDirective } from '../../../directives/image-placeholder.directive';
import { EntityHelperService } from '../../../constants/entity-helper';
import { ChatStateService } from '../../../services/chat-state.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    NgClass,
    MatMenuModule,
    TranslateModule,
    RouterLink,
    RouterLink,
    RouterLinkActive,
    GlobalSearchComponent,
    CommonModule,
    SafeHtmlPipe,
    FormatRolesPipe,
    ImagePlaceholderDirective,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class NavbarComponent implements OnDestroy, ApiErrorHandler, OnInit {
  readonly organizationBlackSVG = orgBlackSVG;
  readonly profileBlackSVG = profileBlackSVG;
  readonly accountSettingSVG = accountSettingSVG;
  readonly helpBlackSVG = helpBlackSVG;
  readonly arrowRightBlackSVG = arrowRightBlackSVG;
  readonly logoutDangerSVG = logoutDangerSVG;
  @ViewChild('imgPlaceholder') userProfileRef!: ElementRef;
  @ViewChild('dropImgPlaceholder')
  dropUserProfileRef!: ElementRef;
  ROLES = ROLES;
  isLoading!: boolean;
  isOpened = false;
  showDropdownList = false;
  showDropdownListForDataset = false;
  dropdownStyle: { [key: string]: string | number } = {};
  isShowGlobalSearch = false;
  logoPath = 'images/ai-kosh-header-logo.svg';
  userProfile: LoggedInUser['user'];
  isAuthResolved = false;
  isOrgAdmin: boolean;
  isContributor: boolean;
  readonly routes = routes;
  private fetchMeriPehchanUrlSub!: Subscription;
  private userToken: any;
  public showWorkshop = true;
  communityId = environment.communityId;

  /** Right margin applied to the navbar so it shrinks on desktop when the chat dock opens. */
  navChatDockMargin = 0;
  private dockOpenSub!: Subscription;
  private dockWidthSub!: Subscription;
  private isChatDockOpen = false;

  renderNavbarContent = true;
  private readonly destroyRef = inject(DestroyRef);
 
  constructor(
    public toaster: ToasterService,
    private apiService: ApiService,
    protected utilService: UtilityService,
    private toasterService: ToasterService,
    private cachingService: CachingService,
    private matDialog: MatDialog,
    private renderer: Renderer2,
    private entitySrv : EntityHelperService,
    private chatState: ChatStateService,
    @Inject(PLATFORM_ID) private platformId: string,
    private analyticsService: AnalyticsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    if (!isPlatformServer(this.platformId)) {
      this.setLogo();
      this.observeThemeChanges();
      this.getUserProfile();
      this.isContributor =
        this.utilService.checkCurrentRole() === ROLES.CONTRIBUTOR;
      this.isOrgAdmin = this.utilService.checkCurrentRole() === ROLES.ADMIN;
            this.subscribeToLanguageChange();

    }

    this.dockOpenSub = this.chatState.isDockOpen$.subscribe((open) => {
      this.isChatDockOpen = open;
      this.updateNavChatDockMargin();
    });
    this.dockWidthSub = this.chatState.dockWidth$.subscribe(() => {
      this.updateNavChatDockMargin();
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateNavChatDockMargin();
  }

  private updateNavChatDockMargin(): void {
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 992;
    this.navChatDockMargin =
      this.isChatDockOpen && this.chatState.dockWidth && isDesktop
        ? this.chatState.dockWidth
        : 0;
  }

  getUserProfile() {
    const loggedInUser = this.cachingService.fetchUserInfo();
    if (loggedInUser) {
      this.cachingService?.loggedInUser.next(loggedInUser);
      this.cachingService?.loggedInUser
        .pipe(
          take(1),
          tap(({ token }) => {
            this.userToken = token;
          }),
        )
        .subscribe();
    }
    this.cachingService?.loggedInUser.subscribe((data) => {
      const user = data?.user;
      this.userProfile = user;
      this.cachingService.userId = user?.id;
      this.getNameInitials();
      this.isAuthResolved = true;
    });
  }

  getNameInitials = () => {
    const fullName = this.userProfile?.fullName?.trim();

    if (!fullName) return;

    const parts = fullName.split(' ');
    const firstInitial = parts[0]?.[0]?.toUpperCase() || '';
    const lastInitial =
      parts.length > 1 ? parts[parts.length - 1]?.[0]?.toUpperCase() : '';

    const nameInitials = firstInitial + lastInitial;

    requestAnimationFrame(() => {
      this.setPlaceHolderImage(nameInitials);
    });
  };

  setPlaceHolderImage(nameInitials: string) {
    const createAndAppendElement = (parentRef: ElementRef) => {
      // Remove any existing <h6> element
      const existingH6 = parentRef?.nativeElement?.querySelector('h6');
      if (existingH6) {
        this.renderer.removeChild(parentRef?.nativeElement, existingH6);
      }

      // Create and append a new <h6> element
      const h4 = this.renderer?.createElement('h6');
      this.renderer?.setStyle(h4, 'margin', '0');
      const text = this.renderer?.createText(nameInitials);
      this.renderer?.appendChild(h4, text);
      this.renderer?.appendChild(parentRef?.nativeElement, h4);
    };

    [this.userProfileRef, this.dropUserProfileRef].forEach(
      createAndAppendElement,
    );
  }

  setLogo() {
    const isDarkMode = document?.body.classList.contains('dark');
    this.logoPath = isDarkMode
      ? 'images/ai-kosh-white-logo.svg' // Dark Mode Logo
      : 'images/ai-kosh-header-logo.svg'; // Light Mode Logo
  }
  observeThemeChanges() {
    const observer = new MutationObserver(() => this.setLogo());
    observer.observe(document?.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  onDropdownClick() {
    const screenWidth = window.innerWidth;
    if (screenWidth >= 992) {
      this.showDropdownList = false;
      return;
    }
    this.showDropdownList = !this.showDropdownList;
  }

  onDropdownClickForDataset() {
    const screenWidth = window.innerWidth;
    if (screenWidth >= 992) {
      this.showDropdownListForDataset = false;
      return;
    }
    this.showDropdownListForDataset = !this.showDropdownListForDataset;
  }

  showDropdown() {
    return this.dropdownStyle;
  }

  onMeriPehchanLogin() {
    this.fetchMeriPehchanUrlSub = this.apiService.fetchNssoAuth().subscribe({
      next: (res) => {
        this.utilService.openExternalPage(res);
      },
      error: apiErrorHandler.bind(this),
    });
  }

  onParicahayLogin() {
    this.fetchMeriPehchanUrlSub = this.apiService
      .fetchParichayAuth()
      .subscribe({
        next: (res) => {
          this.utilService.openExternalPage(res);
        },
        error: apiErrorHandler.bind(this),
      });
  }

  redirectToDashboard() {
    const url = `${environment.webUrl}${environment.webBaseHref}dashboard`;
    this.analyticsService.logEvent('homepage_view');
    this.utilService.openExternalPage(url);
  }

  logoutExistingUser() {
    this.matDialog
      .open(ConfirmationDialogComponent, {
        disableClose: true,
        data: {
          message:
            'Are you sure you want to logout? Please confirm to continue.',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.logoutUser().subscribe({ error: console.error });
        }
      });
  }

  logoutUser() {
    const redirectToLogin = () => {
      window.location.href = `${environment.webUrl}${environment.baseHref}${routes.home}`;
    };

    return this.apiService.logoutUser(this.userToken).pipe(
      tap(() => {
        localStorage.removeItem(cachingKeys.loggedInUser);
        this.utilService.clearFiltersFromStorage();
        this.toasterService.success('Session logged out successfully!');
        redirectToLogin();
      }),
      catchError((error) => {
        this.toasterService.error(error.message);
        return throwError(() => error);
      }),
    );
  }

  onProfileOptionsClicked(action: string) {
    let url;
    switch (action) {
      case 'editUserProfile':
        url = `${environment.webUrl}${environment.webBaseHref}${routes.user}/${routes.editUserProfile}`;
        break;
      case 'editOrg':
        url = `${environment.webUrl}${environment.webBaseHref}${routes.user}/${routes.editOrganization}`;
        break;
      case 'accountSettings':
        url = `${environment.webUrl}${environment.webBaseHref}${routes.user}/${routes.accountSetting}`;
        break;
      case 'faq':
        url = `${environment.webUrl}${environment.webBaseHref}${routes.support}/${routes.faqs}`;
        break;
      case 'userManual':
        url = `${environment.webUrl}${environment.webBaseHref}${routes.support}/${routes.userManual}`;
        break;
      case 'grievanceReport':
        url = `${environment.webUrl}${environment.webBaseHref}${routes.support}/${routes.grievanceReport}`;
        break;
    }

    this.utilService.openExternalPage(url);
  }

  private subscribeToLanguageChange(): void {
    this.cachingService.languageChange$
      .pipe(
        skip(1),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.refreshNavbarContent();
      });
  }

  private refreshNavbarContent(): void {
    // Destroy navbar DOM
   
    this.renderNavbarContent = false;
    this.cdr.detectChanges();

    setTimeout(() => {
     this.renderNavbarContent = true;
      this.cdr.detectChanges();
      this.getNameInitials()
    });
  }
  ngOnDestroy() {
    this.fetchMeriPehchanUrlSub?.unsubscribe();
    this.dockOpenSub?.unsubscribe();
    this.dockWidthSub?.unsubscribe();
      if (!isPlatformServer(this.platformId)) {
    localStorage.removeItem('font-option');
  }
    
  }

  get getGroupLable() {
    return this.entitySrv.isCommunityEntity(this.userProfile.orgDetails.entityType) ? "Community" : "Organization"
  }
}
